import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useSubscription } from "@/hooks/useSubscription";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { 
  CreditCard, 
  Check, 
  Zap, 
  Database, 
  Bot, 
  FileText, 
  Columns, 
  Shield,
  Crown,
  AlertCircle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { StrataLogo } from "@/components/StrataLogo";

interface PlanFeature {
  name: string;
  icon: React.ReactNode;
  free: string | boolean;
  starter: string | boolean;
  pro: string | boolean;
}

const PLAN_FEATURES: PlanFeature[] = [
  { name: "Bases", icon: <Database className="h-4 w-4" />, free: "3", starter: "10", pro: "Unlimited" },
  { name: "Records per table", icon: <Zap className="h-4 w-4" />, free: "1,000", starter: "10,000", pro: "100,000" },
  { name: "AI calls/day", icon: <Bot className="h-4 w-4" />, free: "50", starter: "500", pro: "Unlimited" },
  { name: "Automations", icon: <Zap className="h-4 w-4" />, free: false, starter: "10", pro: "Unlimited" },
  { name: "Forms", icon: <FileText className="h-4 w-4" />, free: false, starter: true, pro: true },
  { name: "Advanced Views", icon: <Columns className="h-4 w-4" />, free: false, starter: true, pro: true },
  { name: "AI Agent", icon: <StrataLogo size="sm" />, free: false, starter: false, pro: true },
  { name: "SSO & 2FA", icon: <Shield className="h-4 w-4" />, free: false, starter: false, pro: true },
];

export function BillingSettings() {
  const { workspaces } = useWorkspaces();
  const activeWorkspace = workspaces?.[0];
  const { subscription, plan, plans, loading, isTrialing, trialDaysRemaining, checkUsage } = useSubscription(activeWorkspace?.id);
  const [aiUsage, setAiUsage] = useState({ current: 0, limit: 50, remaining: 50 });
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // Handle Stripe redirect
  useEffect(() => {
    const success = searchParams.get("success");
    const canceled = searchParams.get("canceled");
    
    if (success === "true") {
      toast.success("Subscription updated successfully!");
    } else if (canceled === "true") {
      toast.info("Checkout was canceled.");
    }
  }, [searchParams]);

  // Fetch current AI usage
  useEffect(() => {
    if (activeWorkspace?.id) {
      checkUsage("ai_call").then(setAiUsage);
    }
  }, [activeWorkspace?.id, checkUsage]);

  const handleUpgrade = async (planId: string) => {
    if (!activeWorkspace?.id) {
      toast.error("No workspace selected");
      return;
    }

    setUpgrading(planId);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          workspaceId: activeWorkspace.id,
          planId,
          successUrl: `${window.location.origin}/settings?tab=billing&success=true`,
          cancelUrl: `${window.location.origin}/settings?tab=billing&canceled=true`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Failed to create checkout session");
      }
    } catch (error: any) {
      console.error("Upgrade error:", error);
      toast.error("Failed to start upgrade process");
    } finally {
      setUpgrading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!activeWorkspace?.id) return;

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          workspaceId: activeWorkspace.id,
          portal: true,
          returnUrl: `${window.location.origin}/settings?tab=billing`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      toast.error("Failed to open billing portal");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const currentPlanId = plan?.id || "free";
  const isUnlimitedAI = aiUsage.limit === -1;
  const aiPercentage = isUnlimitedAI ? 0 : Math.min((aiUsage.current / aiUsage.limit) * 100, 100);

  return (
    <div className="space-y-6">
      {/* Current Plan Status */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Crown className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  {plan?.name || "Free"} Plan
                  {isTrialing && (
                    <Badge variant="secondary" className="bg-amber-500/10 text-amber-600">
                      Trial: {trialDaysRemaining} days left
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {subscription?.current_period_end
                    ? `Renews on ${format(new Date(subscription.current_period_end), "MMM d, yyyy")}`
                    : "No active subscription"}
                </CardDescription>
              </div>
            </div>
            {currentPlanId !== "free" && (
              <Button variant="outline" size="sm" onClick={handleManageSubscription}>
                <CreditCard className="h-4 w-4 mr-2" />
                Manage Billing
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Usage Stats */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">AI Calls Today</span>
                <span className="font-medium">
                  {aiUsage.current}/{isUnlimitedAI ? "∞" : aiUsage.limit}
                </span>
              </div>
              <Progress value={isUnlimitedAI ? 0 : aiPercentage} className="h-2" />
              {aiPercentage >= 80 && !isUnlimitedAI && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Running low on AI calls
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Storage Used</span>
                <span className="font-medium">0 / {plan?.max_storage_mb || 500} MB</span>
              </div>
              <Progress value={0} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plan Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Choose Your Plan</CardTitle>
          <CardDescription>
            Unlock more features and higher limits with a paid plan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {/* Free Plan */}
            <div className={`rounded-xl border-2 p-5 ${currentPlanId === "free" ? "border-primary bg-primary/5" : "border-border"}`}>
              <div className="mb-4">
                <h3 className="font-semibold text-lg">Free</h3>
                <p className="text-2xl font-bold mt-1">₹0<span className="text-sm font-normal text-muted-foreground">/month</span></p>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> 3 bases</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> 1,000 records/table</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> 50 AI calls/day</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Grid view only</li>
              </ul>
              {currentPlanId === "free" ? (
                <Button variant="outline" className="w-full" disabled>
                  Current Plan
                </Button>
              ) : (
                <Button variant="outline" className="w-full" onClick={() => handleUpgrade("free")}>
                  Downgrade
                </Button>
              )}
            </div>

            {/* Starter Plan */}
            <div className={`rounded-xl border-2 p-5 ${currentPlanId === "starter" ? "border-primary bg-primary/5" : "border-border"}`}>
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg">Starter</h3>
                  <Badge variant="secondary" className="bg-primary/10 text-primary">Popular</Badge>
                </div>
                <p className="text-2xl font-bold mt-1">₹1,999<span className="text-sm font-normal text-muted-foreground">/month</span></p>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> 10 bases</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> 10,000 records/table</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> 500 AI calls/day</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> All views + Forms</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> 10 automations</li>
              </ul>
              {currentPlanId === "starter" ? (
                <Button variant="outline" className="w-full" disabled>
                  Current Plan
                </Button>
              ) : (
                <Button 
                  className="w-full" 
                  onClick={() => handleUpgrade("starter")}
                  disabled={upgrading === "starter"}
                >
                  {upgrading === "starter" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upgrade to Starter"}
                </Button>
              )}
            </div>

            {/* Pro Plan */}
            <div className={`rounded-xl border-2 p-5 relative ${currentPlanId === "pro" ? "border-primary bg-primary/5" : "border-border"}`}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                  <StrataLogo size="xs" className="mr-1" /> Best Value
                </Badge>
              </div>
              <div className="mb-4 mt-2">
                <h3 className="font-semibold text-lg">Pro</h3>
                <p className="text-2xl font-bold mt-1">₹4,999<span className="text-sm font-normal text-muted-foreground">/month</span></p>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Unlimited bases</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> 100,000 records/table</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Unlimited AI calls</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> AI Agent + Insights</li>
                <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> SSO & Priority Support</li>
              </ul>
              {currentPlanId === "pro" ? (
                <Button variant="outline" className="w-full" disabled>
                  Current Plan
                </Button>
              ) : (
                <Button 
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600" 
                  onClick={() => handleUpgrade("pro")}
                  disabled={upgrading === "pro"}
                >
                  {upgrading === "pro" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upgrade to Pro"}
                </Button>
              )}
            </div>
          </div>

          {/* Feature Comparison Table */}
          <Separator className="my-8" />
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 font-medium">Feature</th>
                  <th className="text-center py-3 font-medium">Free</th>
                  <th className="text-center py-3 font-medium">Starter</th>
                  <th className="text-center py-3 font-medium">Pro</th>
                </tr>
              </thead>
              <tbody>
                {PLAN_FEATURES.map((feature, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-3 flex items-center gap-2">
                      {feature.icon}
                      {feature.name}
                    </td>
                    <td className="text-center py-3">
                      {typeof feature.free === "boolean" ? (
                        feature.free ? <Check className="h-4 w-4 text-primary mx-auto" /> : <span className="text-muted-foreground">—</span>
                      ) : feature.free}
                    </td>
                    <td className="text-center py-3">
                      {typeof feature.starter === "boolean" ? (
                        feature.starter ? <Check className="h-4 w-4 text-primary mx-auto" /> : <span className="text-muted-foreground">—</span>
                      ) : feature.starter}
                    </td>
                    <td className="text-center py-3">
                      {typeof feature.pro === "boolean" ? (
                        feature.pro ? <Check className="h-4 w-4 text-primary mx-auto" /> : <span className="text-muted-foreground">—</span>
                      ) : feature.pro}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
